// hook to use locker action : openDoor, getPackage, etc

import { v4 as uuid } from "uuid"
import { ActionReq } from "../../../shared/actionReq"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "../../../shared/actionReqData"
import { useCreateThingsMutation } from "../features/things/thingsApi"
type UseLockerAction = {
  lockerId: string
  sstDoorId: string
  sstLockerId: string
  sstReservationId?: string
  reservationId?: string
  tenantId?: string
}

export const useLockerActions = ({
  lockerId,
  sstLockerId,
  sstDoorId,
  tenantId,
  sstReservationId,
  reservationId,
}: UseLockerAction) => {
  const [createThings, { isLoading }] = useCreateThingsMutation()

  if (!tenantId || !lockerId || !sstDoorId) {
    console.error("tenantId, lockerId, sstDoorId are required", {
      tenantId,
      lockerId,
      sstDoorId,
    })
  }

  const openDoor = async (employeeCode?: string) => {
    const f = async () => {
      console.log("openDoor")
      try {
        const openDoorActionReq: Partial<ActionReq> = {
          thingTypeMrn: ActionReqThingTypeMrn,
          executionMode: ActionReqExecutionModeEnum.sync,
          tenantId,
          idempotencyKey: uuid(),
          actionType: ActionReqTypeEnum.SstLockerOpenDoor,
          inputData: {
            tenantId,
            lockerId,
            sstLockerId,
            sstDoorId,
            reservationId,
            employeeCode,
          },
        }
        await createThings({
          body: { things: [{ ...openDoorActionReq }] },
          params: { tenantId },
        }).unwrap()
      } catch (error) {
        console.error("openDoor error", error)
      }
    }
    await f()
  }

  const getPackageWrapper = async (employeeCode?: string) => {
    const getPackageActionReq: Partial<ActionReq> = {
      thingTypeMrn: ActionReqThingTypeMrn,
      executionMode: ActionReqExecutionModeEnum.sync,
      tenantId,
      idempotencyKey: uuid(),
      actionType: ActionReqTypeEnum.SstLockerGetPackage,
      inputData: {
        tenantId,
        lockerId,
        sstLockerId,
        sstDoorId,
        sstReservationId,
        reservationId,
        employeeCode,
      },
    }
    await createThings({
      body: { things: [getPackageActionReq] },
      params: { tenantId },
    }).unwrap()
  }

  const cancelReservation = async (employeeCode?: string) => {
    try {
      if (!sstDoorId) {
        throw new Error("sstDoorId is required to cancelReservation")
      }
      const cancelReservationActionReq: Partial<ActionReq> = {
        thingTypeMrn: ActionReqThingTypeMrn,
        executionMode: ActionReqExecutionModeEnum.sync,
        tenantId,
        idempotencyKey: uuid(),
        actionType: ActionReqTypeEnum.SstLockerCancelReservation,
        inputData: {
          tenantId,
          lockerId,
          sstLockerId,
          sstDoorId,
          sstReservationId,
          reservationId,
          employeeCode,
        },
      }

      await createThings({
        body: { things: [cancelReservationActionReq] },
        params: { tenantId },
      }).unwrap()
    } catch (error) {
      console.error("cancelReservation error", error)
    }
  }

  // LOCKER-377 removed `reserveDoor` / `releaseDoor` from here. They were a
  // second, dead reservation-session create/delete path with zero callers
  // repo-wide — `OsLockerDoorDrawerManager` owns the session lifecycle. A
  // dormant duplicate creator is exactly how the duplicate-session bug comes
  // back, so it is gone rather than left to be rediscovered.

  const simulateExpiration = async (reservationId: string) => {
    const actionReq = {
      thingTypeMrn: ActionReqThingTypeMrn,
      executionMode: ActionReqExecutionModeEnum.sync,
      tenantId,
      idempotencyKey: uuid(),
      actionType: ActionReqTypeEnum.SimulateExpiredReservation,
      inputData: {
        tenantId,
        reservationId,
      },
    }
    await createThings({
      body: { things: [actionReq] },
      params: { tenantId },
    }).unwrap()
  }

  const simulatePayment = async (reservationId: string, amountPaid: number) => {
    const actionReq = {
      thingTypeMrn: ActionReqThingTypeMrn,
      executionMode: ActionReqExecutionModeEnum.sync,
      tenantId,
      idempotencyKey: uuid(),
      actionType: ActionReqTypeEnum.SimulatePaymentReservation,
      inputData: {
        tenantId,
        reservationId,
        amountPaid,
      },
    }
    await createThings({
      body: { things: [actionReq] },
      params: { tenantId },
    }).unwrap()
  }

  const refreshReservation = async (reservationId: string) => {
    const actionReq = {
      thingTypeMrn: ActionReqThingTypeMrn,
      executionMode: ActionReqExecutionModeEnum.sync,
      tenantId,
      idempotencyKey: uuid(),
      actionType: ActionReqTypeEnum.RefreshReservation,
      inputData: {
        tenantId,
        reservationId,
      },
    }
    await createThings({
      body: { things: [actionReq] },
      params: { tenantId },
    }).unwrap()
  }

  return {
    openDoor: async (employeeCode?: string) => {
      await openDoor(employeeCode)
    },
    getPackage: async (employeeCode?: string) => {
      await getPackageWrapper(employeeCode)
    },
    cancelReservation: (employeeCode?: string) => {
      cancelReservation(employeeCode)
    },
    simulateExpiration: (reservationId: string) =>
      simulateExpiration(reservationId),
    simulatePayment: (reservationId: string, amountPaid: number) =>
      simulatePayment(reservationId, amountPaid),
    refreshReservation,
  }
}
